import React, { useEffect, useState } from "react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import "./org-wallet.scss";
import { useNotice } from "../shared/useNotice";
import {
  OrgWallet, OrgWalletSettingsUpdate, OrgOption, SettlementRun, LedgerRow, ClaimRow, Recharge, AuditEntry, SettlementMode,
  RangeSummaryRow, RangeSessionRow,
  listOrgWallets, candidateOrgs, getOrgWallet, updateOrgWalletSettings,
  createSettlement, listSettlements, listLedger, listClaims,
  recordRecharge, listRecharges, approveRecharge, rejectRecharge, listAudit,
  rangeSummary, rangeSessions,
} from "../../../api/orgWallet";

const inr = (n: number) => `₹${(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const dt = (s: string | null) => (s ? new Date(s).toLocaleString("en-IN") : "—");

// Status -> coloured badge, shared across deposit/run/claim/ledger states.
const stateBadge = (s: string) => {
  const v = (s || "").toLowerCase();
  const ok = ["settled", "credited", "processed", "completed"];
  const bad = ["failed", "rejected", "reversed"];
  const warn = ["recorded", "draft", "partial", "pending", "initiated", "approved"];
  const cls = ok.includes(v) ? "ok" : bad.includes(v) ? "bad" : warn.includes(v) ? "warn" : "muted";
  return <span className={`ow-badge ${cls}`}>{s}</span>;
};

// A local calendar day -> UTC ISO. DB/API are UTC-only; the user picks local days,
// the FE expands to a half-open [day 00:00 local, next-day 00:00 local) and converts.
const dayStartUtc = (d: string) => { const [y, m, dd] = d.split("-").map(Number); return new Date(y, m - 1, dd, 0, 0, 0, 0).toISOString(); };
const dayEndExclUtc = (d: string) => { const [y, m, dd] = d.split("-").map(Number); return new Date(y, m - 1, dd + 1, 0, 0, 0, 0).toISOString(); };

type Tab = "deposits" | "settlements" | "ledger" | "claims" | "report" | "audit";

const OrgWalletManagement: React.FC = () => {
  const [orgs, setOrgs] = useState<OrgWallet[]>([]);
  const [selected, setSelected] = useState<OrgWallet | null>(null);
  const [tab, setTab] = useState<Tab>("deposits");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useNotice("error", null);

  // settings form
  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<SettlementMode>("per_transaction");
  const [minBalance, setMinBalance] = useState("0");

  // onboarding (enable a new org)
  const [candidates, setCandidates] = useState<OrgOption[]>([]);
  const [newOrgId, setNewOrgId] = useState("");

  // tab data
  const [deposits, setDeposits] = useState<Recharge[]>([]);
  const [runs, setRuns] = useState<SettlementRun[]>([]);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [claims, setClaims] = useState<ClaimRow[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);

  // deposit form
  const [dAmount, setDAmount] = useState("");
  const [dMethod, setDMethod] = useState("neft");
  const [dRef, setDRef] = useState("");
  const [dNotes, setDNotes] = useState("");
  const [dFile, setDFile] = useState<File | null>(null);

  // settle form
  const [sScope, setSScope] = useState<"monthly" | "manual_range">("monthly");
  const [sPeriodKey, setSPeriodKey] = useState("");
  const [sFrom, setSFrom] = useState("");
  const [sTo, setSTo] = useState("");

  // range report
  const [rFrom, setRFrom] = useState("");
  const [rTo, setRTo] = useState("");
  const [reportRows, setReportRows] = useState<RangeSummaryRow[]>([]);
  const [reportSessions, setReportSessions] = useState<RangeSessionRow[]>([]);
  const [reportLoaded, setReportLoaded] = useState(false);

  const loadOrgs = () => listOrgWallets().then(setOrgs);
  const loadCandidates = () => candidateOrgs().then(setCandidates);

  useEffect(() => {
    listOrgWallets().then(setOrgs).catch((e) => setErr(String(e)));
    candidateOrgs().then(setCandidates).catch(() => {});
  }, []);

  const enableNewOrg = async () => {
    if (!newOrgId) return;
    setBusy(true); setErr(null);
    try {
      const id = Number(newOrgId);
      await updateOrgWalletSettings(id, { enabled: true }); // creates the wallet + sets go-forward cutoff = now
      setNewOrgId("");
      await loadOrgs();
      await loadCandidates();
      await selectOrg(id);
    } catch (e: any) { setErr(e?.response?.data?.detail || String(e)); }
    finally { setBusy(false); }
  };

  const selectOrg = async (orgId: number) => {
    setErr(null);
    try {
      const ov = await getOrgWallet(orgId);
      setSelected(ov);
      setEnabled(ov.enabled);
      setMode(ov.settlement_mode);
      setMinBalance(String(ov.min_balance));
      await loadTab(tab, orgId);
    } catch (e) {
      setErr(String(e));
    }
  };

  const loadTab = async (t: Tab, orgId: number) => {
    if (t === "deposits") setDeposits(await listRecharges(orgId));
    else if (t === "settlements") setRuns(await listSettlements(orgId));
    else if (t === "ledger") setLedger(await listLedger(orgId));
    else if (t === "claims") setClaims(await listClaims(orgId));
    else if (t === "audit") setAudit(await listAudit(orgId));
  };

  const switchTab = async (t: Tab) => {
    setTab(t);
    if (selected) await loadTab(t, selected.org_id);
  };

  const saveSettings = async () => {
    if (!selected) return;
    setBusy(true); setErr(null);
    try {
      const body: OrgWalletSettingsUpdate = { enabled, settlement_mode: mode, min_balance: Number(minBalance) };
      const ov = await updateOrgWalletSettings(selected.org_id, body);
      setSelected(ov);
      setOrgs((prev) => prev.map((o) => (o.org_id === ov.org_id ? ov : o)));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  };

  const submitDeposit = async () => {
    if (!selected) return;
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("amount", dAmount);
      fd.append("method", dMethod);
      if (dRef) fd.append("reference_number", dRef);
      if (dNotes) fd.append("notes", dNotes);
      if (dFile) fd.append("proof", dFile);
      await recordRecharge(selected.org_id, fd);
      setDAmount(""); setDRef(""); setDNotes(""); setDFile(null);
      await loadTab("deposits", selected.org_id);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || String(e));
    } finally { setBusy(false); }
  };

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); if (selected) { await selectOrg(selected.org_id); } }
    catch (e: any) { setErr(e?.response?.data?.detail || String(e)); }
    finally { setBusy(false); }
  };

  const submitSettlement = async () => {
    if (!selected) return;
    await act(async () => {
      await createSettlement(selected.org_id,
        sScope === "monthly"
          ? { scope_type: "monthly", period_key: sPeriodKey }
          // full local days, half-open, converted to UTC on the FE
          : { scope_type: "manual_range", period_start: dayStartUtc(sFrom), period_end: dayEndExclUtc(sTo) });
      await loadTab("settlements", selected.org_id);
    });
  };

  const loadReport = async () => {
    if (!selected || !rFrom || !rTo) return;
    setBusy(true); setErr(null);
    try {
      setReportRows(await rangeSummary(selected.org_id, dayStartUtc(rFrom), dayEndExclUtc(rTo)));
      setReportSessions([]);
      setReportLoaded(true);
    } catch (e: any) { setErr(e?.response?.data?.detail || String(e)); }
    finally { setBusy(false); }
  };

  const loadReportSessions = async (userWalletId?: number) => {
    if (!selected || !rFrom || !rTo) return;
    setBusy(true); setErr(null);
    try {
      setReportSessions(await rangeSessions(selected.org_id, dayStartUtc(rFrom), dayEndExclUtc(rTo), userWalletId));
    } catch (e: any) { setErr(e?.response?.data?.detail || String(e)); }
    finally { setBusy(false); }
  };

  return (
    <MaintenanceLayout title="Org Wallet">
      {err && <div className="maint-form-error">{err}</div>}

      <div className="ow-card">
        <div className="ow-card-title">Enable an organisation</div>
        <div className="ow-form">
          <select value={newOrgId} onChange={(e) => setNewOrgId(e.target.value)}>
            <option value="">Select an org…</option>
            {candidates.map((o) => <option key={o.org_id} value={o.org_id}>{o.org_name} (#{o.org_id})</option>)}
          </select>
          <button className="maint-btn-primary" disabled={busy || !newOrgId} onClick={enableNewOrg}>Enable org wallet</button>
          <span style={{ fontSize: 12, color: "#888" }}>Creates the wallet, enables it, and sets the go-forward cutoff to now.</span>
        </div>
      </div>

      <div className="ow-card">
        <div className="ow-card-title">Organisations</div>
        <table className="maint-table">
          <thead>
            <tr><th>Org</th><th className="ow-right">Balance</th><th className="ow-right">Available</th><th>Status</th><th>Mode</th><th></th></tr>
          </thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.org_id} className="clickable" onClick={() => selectOrg(o.org_id)}>
                <td>{o.org_name} <span className="maint-mono">#{o.org_id}</span></td>
                <td className="ow-right">{inr(o.current_balance)}</td>
                <td className="ow-right">{inr(o.available_balance)}</td>
                <td>{o.enabled ? <span className="ow-badge ok">enabled</span> : <span className="ow-badge muted">disabled</span>}</td>
                <td>{o.settlement_mode}</td>
                <td><button className="maint-btn-small" onClick={(e) => { e.stopPropagation(); selectOrg(o.org_id); }}>Manage</button></td>
              </tr>
            ))}
            {orgs.length === 0 && <tr><td colSpan={6} className="maint-empty">No org wallets yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <>
          <div className="ow-card">
            <div className="ow-detail-head">
              <h3>{selected.org_name}</h3>
              <span className="ow-guid">#{selected.org_id}{selected.org_wallet_guid ? ` · ${selected.org_wallet_guid}` : ""}</span>
            </div>
            <div className="ow-stats">
              <div className="ow-stat"><div className="ow-stat-label">Balance</div><div className="ow-stat-value">{inr(selected.current_balance)}</div></div>
              <div className="ow-stat"><div className="ow-stat-label">Outstanding</div><div className="ow-stat-value">{inr(selected.outstanding)}</div></div>
              <div className={`ow-stat${selected.available_balance < 0 ? " neg" : ""}`}><div className="ow-stat-label">Available</div><div className="ow-stat-value">{inr(selected.available_balance)}</div></div>
            </div>
            <div className="ow-form">
              <label><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enabled (org funds its users' sessions)</label>
              <label>Mode
                <select value={mode} onChange={(e) => setMode(e.target.value as SettlementMode)}>
                  <option value="per_transaction">per_transaction</option>
                  <option value="monthly">monthly</option>
                </select>
              </label>
              <label>Min balance <input value={minBalance} onChange={(e) => setMinBalance(e.target.value)} style={{ width: 90 }} /></label>
              <button className="maint-btn-primary" disabled={busy} onClick={saveSettings}>Save settings</button>
            </div>
          </div>

          <div className="ow-tabs">
            {(["deposits", "settlements", "ledger", "claims", "report", "audit"] as Tab[]).map((t) => (
              <button key={t} className={`ow-tab${tab === t ? " active" : ""}`} onClick={() => switchTab(t)}>{t}</button>
            ))}
          </div>

          <div className="ow-card">
            {tab === "deposits" && (
              <>
                <div className="ow-card-title">Record manual deposit (bank transfer + proof)</div>
                <div className="ow-form">
                  <input placeholder="Amount" value={dAmount} onChange={(e) => setDAmount(e.target.value)} style={{ width: 110 }} />
                  <select value={dMethod} onChange={(e) => setDMethod(e.target.value)}>
                    <option>neft</option><option>imps</option><option>upi</option><option>cheque</option><option>bank_transfer</option><option>other</option>
                  </select>
                  <input placeholder="Txn / UTR ref" value={dRef} onChange={(e) => setDRef(e.target.value)} />
                  <input placeholder="Notes" value={dNotes} onChange={(e) => setDNotes(e.target.value)} />
                  <input type="file" accept="image/*,application/pdf" onChange={(e) => setDFile(e.target.files?.[0] ?? null)} />
                  <button className="maint-btn-primary" disabled={busy} onClick={submitDeposit}>Record</button>
                </div>
                <table className="maint-table">
                  <thead><tr><th>Date</th><th className="ow-right">Amount</th><th>Method</th><th>Ref</th><th>Proof</th><th>Status</th><th>By</th><th></th></tr></thead>
                  <tbody>
                    {deposits.map((d) => (
                      <tr key={d.id}>
                        <td>{dt(d.created_at)}</td><td className="ow-right">{inr(d.amount)}</td><td>{d.method}</td><td>{d.reference_number || "—"}</td>
                        <td>{d.proof_url ? <a href={d.proof_url} target="_blank" rel="noreferrer">view</a> : "—"}</td>
                        <td>{stateBadge(d.status)}</td><td className="maint-mono">{d.recorded_by}</td>
                        <td>{d.status === "recorded" && (
                          <>
                            <button className="maint-btn-small" disabled={busy} onClick={() => act(() => approveRecharge(d.id))}>Approve</button>{" "}
                            <button className="maint-btn-small" disabled={busy} onClick={() => act(() => rejectRecharge(d.id, prompt("Reason?") || undefined))}>Reject</button>
                          </>
                        )}</td>
                      </tr>
                    ))}
                    {deposits.length === 0 && <tr><td colSpan={8} className="maint-empty">No deposits yet.</td></tr>}
                  </tbody>
                </table>
              </>
            )}

            {tab === "settlements" && (
              <>
                <div className="ow-card-title">Settle sessions</div>
                <div className="ow-form">
                  <select value={sScope} onChange={(e) => setSScope(e.target.value as any)}>
                    <option value="monthly">monthly</option>
                    <option value="manual_range">manual_range</option>
                  </select>
                  {sScope === "monthly"
                    ? <input placeholder="YYYY-MM" value={sPeriodKey} onChange={(e) => setSPeriodKey(e.target.value)} />
                    : <><input type="date" value={sFrom} onChange={(e) => setSFrom(e.target.value)} />
                       <input type="date" value={sTo} onChange={(e) => setSTo(e.target.value)} /></>}
                  <button className="maint-btn-primary" disabled={busy} onClick={submitSettlement}>Run settlement</button>
                </div>
                <table className="maint-table">
                  <thead><tr><th>Run</th><th>Scope</th><th>Period</th><th>Status</th><th className="ow-right">Amount</th><th>Sessions</th><th>Users</th><th>By</th><th>Settled</th></tr></thead>
                  <tbody>
                    {runs.map((r) => (
                      <tr key={r.id}>
                        <td className="maint-mono">#{r.id}</td><td>{r.scope_type}</td><td>{r.period_key || `${dt(r.period_start)} → ${dt(r.period_end)}`}</td>
                        <td>{stateBadge(r.status)}</td><td className="ow-right">{inr(r.total_amount)}</td><td>{r.session_count}</td><td>{r.user_count}</td>
                        <td className="maint-mono">{r.triggered_by || "—"}</td><td>{dt(r.settled_at)}</td>
                      </tr>
                    ))}
                    {runs.length === 0 && <tr><td colSpan={9} className="maint-empty">No settlement runs yet.</td></tr>}
                  </tbody>
                </table>
              </>
            )}

            {tab === "ledger" && (
              <table className="maint-table">
                <thead><tr><th>Date</th><th>Type</th><th className="ow-right">Amount</th><th className="ow-right">Before</th><th className="ow-right">After</th><th>State</th></tr></thead>
                <tbody>
                  {ledger.map((l) => (
                    <tr key={l.id}>
                      <td>{dt(l.created_date)}</td><td>{l.transaction_type || "—"}</td><td className="ow-right">{inr(l.transaction_amount)}</td>
                      <td className="ow-right">{inr(l.wallet_balance_before)}</td><td className="ow-right">{inr(l.wallet_balance_after)}</td><td>{stateBadge(l.transaction_state)}</td>
                    </tr>
                  ))}
                  {ledger.length === 0 && <tr><td colSpan={6} className="maint-empty">No ledger entries.</td></tr>}
                </tbody>
              </table>
            )}

            {tab === "claims" && (
              <table className="maint-table">
                <thead><tr><th>Date</th><th>User</th><th className="ow-right">Amount</th><th>State</th><th>Run</th><th>Period</th></tr></thead>
                <tbody>
                  {claims.map((c) => (
                    <tr key={c.id}>
                      <td>{dt(c.created_at)}</td><td>{c.user_name || c.user_wallet_id}</td><td className="ow-right">{inr(c.claim_amount)}</td>
                      <td>{stateBadge(c.claim_state)}</td><td>{c.settlement_run_id ?? "—"}</td>
                      <td>{c.period_start ? `${dt(c.period_start)} → ${dt(c.period_end)}` : "—"}</td>
                    </tr>
                  ))}
                  {claims.length === 0 && <tr><td colSpan={6} className="maint-empty">No reimbursements yet.</td></tr>}
                </tbody>
              </table>
            )}

            {tab === "report" && (
              <>
                <div className="ow-card-title">Range report (user wallet–wise) — full local days, sent as UTC</div>
                <div className="ow-form">
                  <input type="date" value={rFrom} onChange={(e) => setRFrom(e.target.value)} />
                  <input type="date" value={rTo} onChange={(e) => setRTo(e.target.value)} />
                  <button className="maint-btn-primary" disabled={busy} onClick={loadReport}>Load</button>
                  <button className="maint-btn-secondary" disabled={busy || !reportLoaded} onClick={() => loadReportSessions(undefined)}>View all sessions</button>
                </div>
                {reportLoaded && (
                  <table className="maint-table">
                    <thead><tr><th>User</th><th>Phone</th><th>Sessions</th><th className="ow-right">Total</th><th>Unsettled</th><th></th></tr></thead>
                    <tbody>
                      {reportRows.map((r) => (
                        <tr key={r.user_wallet_id}>
                          <td>{r.user_name || r.user_wallet_id}</td><td>{r.phone || "—"}</td>
                          <td>{r.session_count}</td><td className="ow-right">{inr(r.total_amount)}</td>
                          <td>{r.unsettled_count} · {inr(r.unsettled_amount)}</td>
                          <td><button className="maint-btn-small" disabled={busy} onClick={() => loadReportSessions(r.user_wallet_id)}>Sessions</button></td>
                        </tr>
                      ))}
                      {reportRows.length === 0 && <tr><td colSpan={6} className="maint-empty">No charging in this range.</td></tr>}
                    </tbody>
                  </table>
                )}
                {reportSessions.length > 0 && (
                  <table className="maint-table" style={{ marginTop: 16 }}>
                    <thead><tr><th>Date</th><th>User</th><th>Station</th><th className="ow-right">Amount</th><th>Settled</th><th>Session</th></tr></thead>
                    <tbody>
                      {reportSessions.map((s) => (
                        <tr key={s.session_debit_txn_id}>
                          <td>{dt(s.created_date)}</td><td>{s.user_name || s.user_wallet_id}</td>
                          <td>{s.station_name || "—"}</td><td className="ow-right">{inr(s.amount)}</td>
                          <td>{s.settled ? <span className="ow-badge ok">yes</span> : <span className="ow-badge muted">no</span>}</td>
                          <td className="maint-mono">{s.session_guid || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {tab === "audit" && (
              <table className="maint-table">
                <thead><tr><th>Date</th><th>Actor</th><th>Action</th><th>Target</th><th>Detail</th></tr></thead>
                <tbody>
                  {audit.map((a) => (
                    <tr key={a.id}>
                      <td>{dt(a.created_at)}</td><td className="maint-mono">{a.actor}</td><td>{a.action}</td>
                      <td>{a.target_type}{a.target_id ? ` #${a.target_id}` : ""}</td>
                      <td className="maint-mono">{a.detail ? JSON.stringify(a.detail) : ""}</td>
                    </tr>
                  ))}
                  {audit.length === 0 && <tr><td colSpan={5} className="maint-empty">No audit entries.</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </MaintenanceLayout>
  );
};

export default OrgWalletManagement;
